<template>
  <div class="fixed inset-0 bg-slate-900/60 z-50 flex items-center justify-center p-4" @click.self="$emit('close')" role="dialog" aria-modal="true" :aria-label="$t('図形を描き込む')">
    <div class="bg-white rounded-q4cl shadow-card-hover w-full max-w-4xl border border-q4cl-border flex flex-col max-h-[90vh]">
      <header class="px-5 py-3 border-b border-q4cl-border flex items-center justify-between flex-shrink-0">
        <h3 class="font-bold text-q4cl-primary">{{ $t('図形を描き込む') }}</h3>
        <button class="btn-icon" type="button" @click="$emit('close')" :aria-label="$t('閉じる')"><i class="fa-solid fa-xmark" aria-hidden="true"></i></button>
      </header>

      <div class="flex-1 overflow-auto p-6">
        <div class="flex items-center justify-center min-h-full">
          <div ref="containerRef" class="relative bg-white border border-q4cl-border rounded-q4cl shadow-inner">
            <canvas ref="bgCanvasRef" class="absolute top-0 left-0 rounded-q4cl" style="pointer-events: none; z-index: 1; display: block;" aria-hidden="true"></canvas>
            <canvas ref="canvasRef" class="absolute top-0 left-0 cursor-crosshair rounded-q4cl" style="z-index: 2; touch-action: none; user-select: none; -webkit-user-select: none; display: block;" :aria-label="$t('描画キャンバス')"></canvas>
          </div>
        </div>
      </div>

      <div class="px-5 py-3 border-t border-q4cl-border bg-q4cl-surface flex flex-wrap items-center gap-3 text-xs text-q4cl-muted flex-shrink-0">
        <div class="flex items-center gap-2" role="radiogroup" :aria-label="$t('色')">
          <span>{{ $t('色') }}</span>
          <button
            v-for="c in palette"
            :key="c.value"
            type="button"
            @click="pickColor(c.value)"
            :class="['w-7 h-7 rounded-full border-2 transition-all hover:scale-110',
              (!isErasing && color === c.value) ? 'border-q4cl-primary ring-2 ring-offset-1 ring-q4cl-accent' : 'border-q4cl-border']"
            :style="{ backgroundColor: c.value }"
            :title="$t(c.label)"
            :aria-label="$t(c.label)"
            :aria-pressed="!isErasing && color === c.value"
          ></button>
        </div>
        <label class="flex items-center gap-1">{{ $t('太さ') }}
          <input type="range" min="1" max="20" step="1" v-model.number="weight" class="accent-q4cl-accent" :aria-label="$t('太さ')" />
          <span class="font-bold text-q4cl-primary w-6 text-right" aria-hidden="true">{{ weight }}</span>
        </label>
        <button type="button" class="btn-secondary !py-1 !px-3" :class="isErasing ? '!bg-amber-100 !border-amber-500 !text-amber-800' : ''" @click="isErasing = !isErasing" :aria-pressed="isErasing">
          <i class="fa-solid mr-1" :class="isErasing ? 'fa-eraser' : 'fa-pen'" aria-hidden="true"></i>{{ isErasing ? $t('消しゴム') : $t('描画') }}
        </button>
      </div>

      <footer class="px-5 py-3 border-t border-q4cl-border flex justify-end gap-2 flex-shrink-0 flex-wrap">
        <button type="button" class="btn-secondary" @click="clear">{{ $t('クリア') }}</button>
        <button type="button" class="btn-secondary" @click="$emit('close')">{{ $t('キャンセル') }}</button>
        <button type="button" class="btn-primary" @click="save">{{ $t('確定') }}</button>
      </footer>
    </div>
  </div>
</template>

<script>
/**
 * 図形描画モーダル — 旧 Q4CL-client/ModalDraw.vue 踏襲。
 * 背景画像レイヤー (bgCanvas) と描画レイヤー (canvas) の 2 層構造。
 * mouse / touch のネイティブイベントで描画 (atrament は使わない)。
 * 消しゴムは描画レイヤーに対してのみ destination-out。
 */
export default {
  name: 'ModalDraw',
  props: {
    baseImg: { type: String, default: '' },
    initial: { type: String, default: '' }
  },
  emits: ['close', 'save'],
  setup(props, { emit }) {
    const canvasRef = Vue.ref(null);
    const bgCanvasRef = Vue.ref(null);
    const containerRef = Vue.ref(null);
    const palette = [
      { value: '#000000', label: '黒' },
      { value: '#e74c3c', label: '赤' },
      { value: '#3498db', label: '青' },
      { value: '#1abc9c', label: '緑' }
    ];
    const color = Vue.ref('#1abc9c');
    const weight = Vue.ref(10);
    const isErasing = Vue.ref(false);
    function pickColor(c) { color.value = c; isErasing.value = false; }

    let isDrawing = false;
    let lastX = 0, lastY = 0;

    function getMaxBoxSize() {
      // 旧版踏襲 (Q4CL-client/components/ModalDraw.vue:288-289):
      // max-w-4xl (896px) のモーダルに収まるよう window.innerWidth - 100 上限 800px、
      // 縦は window.innerHeight * 0.6 上限 600px。
      return {
        maxW: Math.min(window.innerWidth - 100, 800),
        maxH: Math.min(window.innerHeight * 0.6, 600)
      };
    }

    function applyCanvasSize(w, h) {
      const canvas = canvasRef.value;
      const bg = bgCanvasRef.value;
      const container = containerRef.value;
      canvas.width = w; canvas.height = h;
      canvas.style.width = w + 'px'; canvas.style.height = h + 'px';
      bg.width = w; bg.height = h;
      bg.style.width = w + 'px'; bg.style.height = h + 'px';
      container.style.width = w + 'px'; container.style.height = h + 'px';
    }

    // 旧旧版踏襲 (Q4CL-client_old_version.xml:3641-3661):
    // 外部 URL (http/https) の画像はさくらの proxy API を介して base64 (data:) 化してから
    // <img> に渡す。 これで canvas が tainted にならず toDataURL が成功する。
    // (さくらサーバが CORS ヘッダを返さないので、 直接 <img> 読込み + canvas 描画では
    //  必ず SecurityError になる。 旧旧版はこの API 経由で回避していた)
    const PROXY_API = 'https://api.emuyn.net/getImgsrcFromImgfile.php';

    async function fetchAsDataUrl(rawUrl) {
      if (!rawUrl) return '';
      // 既に data: URL なら何もしない
      if (rawUrl.startsWith('data:')) return rawUrl;
      // 同一オリジンの相対パス / 同一 origin URL はそのまま (proxy 不要)
      try {
        const u = new URL(rawUrl, location.href);
        if (u.origin === location.origin) return rawUrl;
      } catch (e) { return rawUrl; }
      // 外部 origin → proxy 経由で base64 化
      try {
        const res = await fetch(PROXY_API + '?fileurl=' + encodeURIComponent(rawUrl));
        if (!res.ok) throw new Error('proxy ' + res.status);
        const text = (await res.text()).trim();
        if (text.startsWith('data:')) return text;
        // svg 等が生で返るケースは、 旧旧版同様 canvas に描画して dataURL 化
        const tmp = document.createElement('canvas');
        // 元の (Canvg などのライブラリを噛ませる代わりに) 一旦 img 経由で読む
        return await new Promise((resolve) => {
          const i = new Image();
          i.onload = () => {
            tmp.width = i.naturalWidth; tmp.height = i.naturalHeight;
            tmp.getContext('2d').drawImage(i, 0, 0);
            try { resolve(tmp.toDataURL('image/png')); } catch (e) { resolve(rawUrl); }
          };
          i.onerror = () => resolve(rawUrl);
          i.src = 'data:image/svg+xml;utf8,' + encodeURIComponent(text);
        });
      } catch (e) {
        console.warn('[ModalDraw] proxy 経由 base64 化失敗:', e, '→ 生 URL で続行');
        return rawUrl;
      }
    }

    async function loadBaseImageAndSize() {
      const { maxW, maxH } = getMaxBoxSize();
      if (!props.baseImg) {
        applyCanvasSize(maxW, maxH);
        const bgCtx = bgCanvasRef.value.getContext('2d');
        bgCtx.fillStyle = '#ffffff';
        bgCtx.fillRect(0, 0, maxW, maxH);
        return;
      }
      const resolvedSrc = await fetchAsDataUrl(props.baseImg);
      await new Promise((resolve) => {
        const img = new Image();
        img.onload = () => {
          const ratio = img.width / img.height;
          let w = maxW, h = w / ratio;
          if (h > maxH) { h = maxH; w = h * ratio; }
          applyCanvasSize(w, h);
          const bgCtx = bgCanvasRef.value.getContext('2d');
          bgCtx.fillStyle = '#ffffff';
          bgCtx.fillRect(0, 0, w, h);
          bgCtx.drawImage(img, 0, 0, w, h);
          resolve();
        };
        img.onerror = () => {
          console.warn('[ModalDraw] 背景画像読込み失敗:', props.baseImg);
          applyCanvasSize(maxW, maxH);
          const bgCtx = bgCanvasRef.value.getContext('2d');
          bgCtx.fillStyle = '#ffffff';
          bgCtx.fillRect(0, 0, maxW, maxH);
          resolve();
        };
        img.src = resolvedSrc;
      });
    }

    async function loadInitialIfAny() {
      if (!props.initial) return;
      // initial が外部 URL のままだと canvas が tainted → toDataURL 失敗。
      // 旧旧版同様 proxy 経由で base64 化してから描画する。
      const resolved = await fetchAsDataUrl(props.initial);
      const canvas = canvasRef.value;
      await new Promise((resolve) => {
        const img = new Image();
        img.onload = () => {
          canvas.getContext('2d').drawImage(img, 0, 0, canvas.width, canvas.height);
          resolve();
        };
        img.onerror = () => resolve();
        img.src = resolved;
      });
    }

    function pointerXY(e) {
      const rect = canvasRef.value.getBoundingClientRect();
      const p = e.touches ? e.touches[0] : e;
      return { x: p.clientX - rect.left, y: p.clientY - rect.top };
    }

    function drawLine(x1, y1, x2, y2) {
      const ctx = canvasRef.value.getContext('2d');
      ctx.beginPath();
      ctx.moveTo(x1, y1);
      ctx.lineTo(x2, y2);
      if (isErasing.value) {
        ctx.globalCompositeOperation = 'destination-out';
        ctx.lineWidth = weight.value * 2;
      } else {
        ctx.globalCompositeOperation = 'source-over';
        ctx.strokeStyle = color.value;
        ctx.lineWidth = weight.value;
      }
      ctx.lineCap = 'round';
      ctx.lineJoin = 'round';
      ctx.stroke();
    }

    function onDown(e) {
      if (e.cancelable) e.preventDefault();
      isDrawing = true;
      const { x, y } = pointerXY(e);
      lastX = x; lastY = y;
      drawLine(x, y, x + 0.01, y + 0.01); // 点単発でも描けるよう微小線
    }
    function onMove(e) {
      if (!isDrawing) return;
      if (e.cancelable) e.preventDefault();
      const { x, y } = pointerXY(e);
      drawLine(lastX, lastY, x, y);
      lastX = x; lastY = y;
    }
    function onUp() { isDrawing = false; }

    function attachListeners() {
      const c = canvasRef.value;
      c.addEventListener('mousedown', onDown);
      c.addEventListener('mousemove', onMove);
      c.addEventListener('mouseup', onUp);
      c.addEventListener('mouseleave', onUp);
      c.addEventListener('touchstart', onDown, { passive: false });
      c.addEventListener('touchmove', onMove, { passive: false });
      c.addEventListener('touchend', onUp);
    }
    function detachListeners() {
      const c = canvasRef.value;
      if (!c) return;
      c.removeEventListener('mousedown', onDown);
      c.removeEventListener('mousemove', onMove);
      c.removeEventListener('mouseup', onUp);
      c.removeEventListener('mouseleave', onUp);
      c.removeEventListener('touchstart', onDown);
      c.removeEventListener('touchmove', onMove);
      c.removeEventListener('touchend', onUp);
    }

    function clear() {
      const c = canvasRef.value;
      c.getContext('2d').clearRect(0, 0, c.width, c.height);
    }

    function save() {
      const canvas = canvasRef.value;
      const bg = bgCanvasRef.value;
      const temp = document.createElement('canvas');
      temp.width = canvas.width;
      temp.height = canvas.height;
      const t = temp.getContext('2d');
      t.drawImage(bg, 0, 0);
      t.drawImage(canvas, 0, 0);
      emit('save', temp.toDataURL('image/jpeg', 0.85));
    }

    Vue.onMounted(async () => {
      await Vue.nextTick();
      await loadBaseImageAndSize();
      await loadInitialIfAny();
      attachListeners();
    });
    Vue.onBeforeUnmount(() => {
      detachListeners();
    });

    return { canvasRef, bgCanvasRef, containerRef, color, weight, isErasing, palette, pickColor, clear, save };
  }
};
</script>
